import React, { useEffect, useState, useCallback } from 'react';
import { graphql, compose } from 'react-apollo';
import { branch, renderComponent, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import LoadingPage from 'components/common/Loader/Spinner/Spinner';
import { Paper, makeStyles } from '@material-ui/core';
import '../Home/Home.scss';
import { Button } from '@mui/material';
import { decodeHtml } from 'utils/htmlEncoderDecoder';
import { useViewport } from 'utils/getViewport/getViewport';
import { isFetchingAssessments, getAssessments } from 'selectors/assessments/selector';
import { listAssessments, getDomainRestrictedTemplates } from 'graphql/operations';
import { getDomainRestrictedTemplatesSelector } from 'selectors/templates/selector';
import { NETWORK_ONLY } from 'graphql/fetchPolicies';
import MobileAssessmentsTable from 'components/threepp/home/MobileAssessmentsTable/MobileAssessmentsTable';
import { ControlledSection } from 'components/common/ControlledSection/ControlledSection';
import { EngageGrid } from 'components/common/EngageGrid/EngageGrid';
import { AssessmentProgressBar } from 'components/threepp/AssessmentProgressBar/AssessmentProgressBar';
import Tooltip from 'components/common/Tooltip/Tooltip';
import { getFormattedDueDate, getFormattedSubmitDate, formatOwners } from 'pages/utils/page-utils';
import { handleProgressSort, handleDueSort } from 'components/common/EngageGrid/helpers/customSortHelpers';
import CreateNewModal from 'components/threepp/CreateNewModal/CreateNewModal';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';
import HomeGuidedTour, { SHOW_HOME_GUIDED_TOUR_KEY } from 'components/threepp/home/HomeGuidedTour/HomeGuidedTour';
import i18n from '../../i18n';

const useStyles = makeStyles(() => ({
  gridContainer: { padding: '1rem', '& +$gridContainer': { marginTop: '1rem' } },
  assessmentLink: {
    display: 'inline!important',
    backgroundColor: 'inherit!important',
    padding: '0!important',
    width: '96%',
    '& div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'left',
      fontWeight: '400',
      '&:hover': { textDecoration: 'underline' }
    },
    '&:focus': { outline: '0', textDecoration: 'underline' }
  }
}));

export const CustomerRequestsBase = props => {
  const { translate, assessments, templateData, history } = props;
  const { gridContainer, assessmentLink } = useStyles();
  const { isMobile } = useViewport();
  const [showCreateNewModal, setCreateNewModal] = useState(false);
  const localStoreGuidedTourState = localStorage.getItem(SHOW_HOME_GUIDED_TOUR_KEY);
  const triggerGuidedTourState = localStoreGuidedTourState === null ? true : JSON.parse(localStoreGuidedTourState);
  const [triggerGuidedTour, setTriggerGuidedTour] = useState(triggerGuidedTourState);
  const createNewButtonRef = React.useRef(null);

  const onCreateNewModalToggle = () => {
    setCreateNewModal(!showCreateNewModal);
  };
  const handleOnClick = useCallback(
    id => {
      history.push({ pathname: `/assessments/${id}` });
    },
    [history]
  );

  const setGuidedTourState = state => setTriggerGuidedTour(state);

  useEffect(() => {
    EventEmitter.subscribe(EventEmitterTypes.SHOW_HOME_GUIDED_TOUR, ({ state }) => setGuidedTourState(state));
    return () => EventEmitter.unsubscribe(EventEmitterTypes.SHOW_HOME_GUIDED_TOUR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter Assessments
  const customerAssessments = assessments?.filter(assessment => !assessment.productIdentifier || assessment.productIdentifier === 'E4V');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const openAssessments = customerAssessments?.filter(a => a.status === 'NEW' || a.status === 'INCOMPLETE') || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submittedAssessments =
    customerAssessments?.filter(a => ['SUBMITTED', 'SUBMITTED_SYNC_SUCCESS', 'SUBMITTED_SYNC_FAILED'].includes(a.status)) || [];
  // EngageGrid Props
  const openAssessmentGridProps = React.useMemo(
    () => ({
      translate,
      columns: [
        { name: 'identifier', title: translate('model.assessment.identifier') },
        { name: 'name', title: translate('model.assessment.name') },
        { name: 'due', title: translate('model.assessment.dueDate') },
        { name: 'company', title: translate('model.assessment.company') },
        { name: 'owner', title: translate('model.assessment.owners') },
        { name: 'progress', title: translate('model.assessment.progress') }
      ],
      rows: openAssessments.map(assessment => {
        const { identifier, recordId, name, id, dueDate, company, owners, metadata } = assessment;
        const { answeredFields, totalFields } = metadata;
        return {
          identifier: (
            <Tooltip title={decodeHtml(identifier || recordId)}>
              <Button role="link" onClick={() => handleOnClick(id)} className={assessmentLink} disableRipple>
                <div>{decodeHtml(identifier || recordId)}</div>
              </Button>
            </Tooltip>
          ),
          name: decodeHtml(name),
          due: getFormattedDueDate(translate, dueDate),
          owner: formatOwners(owners),
          company: decodeHtml(company),
          progress: <AssessmentProgressBar className="col-completed" answered={answeredFields} total={totalFields} />
        };
      }),
      isFilteringEnabled: true,
      isSortingEnabled: true,
      defaultSorting: [{ columnName: 'due', direction: 'asc' }],
      columnSortingFnOverrides: [
        { columnName: 'due', compare: handleDueSort },
        { columnName: 'progress', compare: handleProgressSort }
      ],
      columnFilteringState: [
        {
          columnName: 'identifier',
          filteringEnabled: false
        },
        {
          columnName: 'due',
          filteringEnabled: false
        },
        {
          columnName: 'progress',
          filteringEnabled: false
        }
      ]
    }),
    [translate, openAssessments, assessmentLink, handleOnClick]
  );

  const submittedAssessmentGridProps = React.useMemo(
    () => ({
      translate,
      columns: [
        { name: 'identifier', title: translate('model.assessment.identifier') },
        { name: 'name', title: translate('model.assessment.name') },
        { name: 'submitted', title: translate('model.assessment.submitted') },
        { name: 'company', title: translate('model.assessment.company') },
        { name: 'owner', title: translate('model.assessment.owners') }
      ],
      rows: submittedAssessments.map(assessment => {
        const { identifier, recordId, name, id, company, owners, submitDate } = assessment;
        return {
          identifier: (
            <Tooltip title={decodeHtml(identifier || recordId)}>
              <Button role="link" onClick={() => handleOnClick(id)} className={assessmentLink} disableRipple>
                <div>{decodeHtml(identifier || recordId)}</div>
              </Button>
            </Tooltip>
          ),
          name: decodeHtml(name),
          owner: formatOwners(owners),
          company: decodeHtml(company),
          submitted: getFormattedSubmitDate(translate, submitDate, true)
        };
      }),
      isFilteringEnabled: true,
      isSortingEnabled: true,
      defaultSorting: [{ columnName: 'submitted', direction: 'desc' }],
      columnFilteringState: [
        {
          columnName: 'identifier',
          filteringEnabled: false
        }
      ]
    }),
    [assessmentLink, handleOnClick, submittedAssessments, translate]
  );

  const createNewRecordButton = (
    <Button
      id="createNewRecordButton"
      className="create-new-button"
      variant="contained"
      disableElevation
      onClick={onCreateNewModalToggle}
      lang={i18n.language}
      ref={createNewButtonRef}
    >
      {translate('home.createNewRecord')}
    </Button>
  );

  return (
    <div className={isMobile ? 'home-page mobile-home-page' : 'row home-page '}>
      {triggerGuidedTour && <HomeGuidedTour setTriggerGuidedTour={setGuidedTourState} isMobile={isMobile} createNewButtonRef={createNewButtonRef} />}
      <div className={isMobile ? 'mobile-home-paper' : 'col-md-9 main-page-content'}>
        <div className="title-button-container">
          <h5 className="paper-title">{translate('customerRequests.title')}</h5>
          {templateData.length ? createNewRecordButton : ''}
        </div>
        <Paper className={gridContainer} elevation={3}>
          <ControlledSection name={`${translate('home.openAssessments')} (${openAssessments.length})`}>
            {!isMobile ? <EngageGrid {...openAssessmentGridProps} /> : <MobileAssessmentsTable assessments={openAssessments} />}
          </ControlledSection>
        </Paper>

        {submittedAssessments.length > 0 && (
          <Paper className={gridContainer} elevation={3}>
            <ControlledSection name={`${translate('home.submittedAssessments')} (${submittedAssessments.length})`} expanded={false}>
              {!isMobile ? <EngageGrid {...submittedAssessmentGridProps} /> : <MobileAssessmentsTable assessments={submittedAssessments} />}
            </ControlledSection>
          </Paper>
        )}
      </div>
      <CreateNewModal toggle={onCreateNewModalToggle} isOpen={showCreateNewModal} />
    </div>
  );
};

export const CustomerRequestsHOC = compose(
  withRouter,
  withTranslation(),
  graphql(listAssessments, { options: { fetchPolicy: NETWORK_ONLY } }),
  graphql(getDomainRestrictedTemplates, { name: 'getDomainRestrictedTemplates' }),
  mapProps(props => ({
    translate: props.t,
    history: props.history,
    listAssessments: props.listAssessments,
    isLoading: isFetchingAssessments(props),
    assessments: getAssessments(props),
    templateData: getDomainRestrictedTemplatesSelector(props)
  })),
  branch(
    ({ isLoading }) => isLoading,
    renderComponent(() => <LoadingPage size="lg" expanded />)
  )
);

export const CustomerRequests = CustomerRequestsHOC(CustomerRequestsBase);
